import { Component, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UserService } from '../services/User.service';
import { ANGULAR_MODULES } from '../Shared/shared-imports';
import { LoaderComponent } from '../Shared/loader/loader.component';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { GeneralService } from 'src/app/services/general.service';
import { TenantService } from '../services/tenant.service';
import { PushNotificationService } from '../services/push-notification.service';
import { Capacitor } from '@capacitor/core';
import { WordReplacementService } from '../services/word-replacement.service';
import { SharedHTMLSettingsDataService } from '../Shared/sharedHTMLSettingsData.service';

@Component({
  selector: 'app-login',
  imports: [ANGULAR_MODULES, LoaderComponent],
  standalone: true,
  templateUrl: './login.html',
  styleUrl: './login.scss'
})
export class Login {
  loginForm: FormGroup;
  marketId: string;
  public loaderIcon: Promise<boolean>;
  displayUsernameError = false;
  displayPasswordError = false;
  loginError = false;
  inActive = false;
  confirmMailInput: HTMLInputElement;
  toasterConfig = {
    closeButton: true,
    timeOut: 4500,
    easing: 'ease-out',
    progressBar: true,
    positionClass: 'toast-top-right',
    tapToDismiss: true,
    showEasing: 'swing',
    hideEasing: 'linear',
    showMethod: 'fadeIn',
    hideMethod: 'slideUp'
  };
  showPassword = false;
  IsAccountDeactivated = false;
  marketName = '';
  isNative = Capacitor.isNativePlatform();

  constructor(
    private renderer: Renderer2,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private toastr: ToastrService,
    private generalService: GeneralService,
    private tenantService: TenantService,
    private wordReplacementService: WordReplacementService,
    private pushNotificationService: PushNotificationService,
    private sharedService: SharedHTMLSettingsDataService
  ) {
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required]],
      password: ['', Validators.required],
      rememberMe: [false]
    });
  }

  ngOnInit(): void {
    if (Capacitor.isNativePlatform()) {
      // On native, read marketId from TenantService (set during onboarding).
      const cfg = this.tenantService.getConfig();
      this.marketId = cfg?.marketId ?? '';
      this.marketName = cfg?.profileName || cfg?.marketName || '';
    } else {
      // On web, read marketId from the URL parameter (legacy behaviour).
      this.route.params.subscribe((params: Params) => {
        this.marketId = params['marketId'];
      });
    }

    this.renderer.setStyle(document.body, 'min-height', '100vh');
    this.renderer.setStyle(document.body, 'background', 'linear-gradient(180deg,#0b0b1f,#1a1a3c)');
    this.renderer.setStyle(document.body, 'display', 'flex');
    this.renderer.setStyle(document.body, 'align-items', 'center');
    this.renderer.setStyle(document.body, 'justify-content', 'center');
    this.renderer.setStyle(document.body, 'font-family', 'Arial, sans-serif');
  }

  validateLoginCredentials(): void {
    if (!this.loginForm.valid) {
      this.displayUsernameError = this.loginForm.get('username').hasError('required');
      this.displayPasswordError = this.loginForm.get('password').hasError('required');
    } else {
      this.displayUsernameError = false;
      this.displayPasswordError = false;
    }
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  switchMarket(): void {
    this.router.navigate(['/switch-profile']);
  }

  validateLogin(): void {
    this.loaderIcon = Promise.resolve(true);
    if (this.loginForm.valid) {
      this.loginError = false;
      const credentials = {
        username: this.loginForm.value['username'],
        password: this.loginForm.value['password'],
        MarketId: this.marketId
      };

      this.userService.getToken(credentials).subscribe({
        next: async (response) => {
          if (response) {
            if (!response.InActive) {
              this.loginError = false;
              const rememberMe = this.loginForm.value['rememberMe'];
              if (rememberMe) {
                localStorage.setItem(`rememberMe_${this.marketId}`, 'true');
                localStorage.setItem(`username_${this.marketId}_EmailAdd`, this.loginForm.value['username']);
              } else {
                localStorage.removeItem(`rememberMe_${this.marketId}`);
                localStorage.removeItem(`username_${this.marketId}_EmailAdd}`);
              }
              const vendorId = response.UserId;
              this.userService.setSessionData(this.marketId, vendorId);
              this.generalService.clearVendorCache();
              sessionStorage.setItem('token', response.token);

              if (Capacitor.isNativePlatform()) {
                const cfg = this.tenantService.getConfig();
                if (cfg) {
                  const id = TenantService.computeProfileId(cfg.apiBaseUrl, this.marketId);
                  await this.tenantService.upsertProfile({
                    id,
                    apiBaseUrl: cfg.apiBaseUrl,
                    marketId: this.marketId,
                    marketName: cfg.marketName,
                    syncfusionKey: cfg.syncfusionKey,
                    vendorId,
                    token: response.Token,
                    lastUsedAt: Date.now()
                  });
                  await this.tenantService.switchToProfile(id);
                }
              }

              this.getASCloudLicenseSettings();
              this.getTermsUsedInMarket();

              // Register device for push notifications (native only — no-op on web)
              await this.pushNotificationService.initialize(vendorId);

              this.router.navigate(['/ops/dashboard']);
            } else {
              this.inActive = response.InActive;
            }
            this.loaderIcon = Promise.resolve(false);
          } else {
            this.loginError = true;
            this.loaderIcon = Promise.resolve(false);
          }
        },
        error: (err) => {
          this.loaderIcon = Promise.resolve(false);
          this.loginError = false;
          this.sharedService.handleApiError(err, 'Unable to login. Please try again.');
        }
      });
    } else {
      this.displayUsernameError = this.loginForm.get('username').hasError('required');
      this.displayPasswordError = this.loginForm.get('password').hasError('required');
      this.loaderIcon = Promise.resolve(false);
    }
  }

  getASCloudLicenseSettings(): void {
    this.generalService.getASCloudLicenseSettings(Number(this.marketId)).pipe(take(1)).subscribe();
  }

  getTermsUsedInMarket(): void {
    this.generalService
      .getTermsUsedInMarket(Number(this.marketId))
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.wordReplacementService.setTermMap({
            booth: res.BoothTerm,
            vendor: res.VendorTerm,
            market: res.MarketTerm
          });
        }
      });
  }

  ForgotPassword(event: Event): void {
    event.preventDefault();
    Swal.fire<boolean>({
      title: 'Confirm Mail',
      html: `
        <div style="max-width:340px; margin:0 auto; text-align:left;">
          <input type="text" id="email" class="swal2-input" style="width:100%; margin:0 0 14px 0; box-sizing:border-box;" placeholder="Email">
          <div style="display:flex; align-items:flex-start; gap:8px; font-size:13px; color:#106EBE; line-height:1.5; margin-bottom:8px;">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#106EBE" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0; margin-top:2px; display:block;">
              <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
            </svg>
            <span style="flex:1;">Your new login password will be sent to your registered email address.</span>
          </div>
          <div style="display:flex; align-items:flex-start; gap:8px; font-size:13px; color:#dc3545; line-height:1.5;">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0; margin-top:2px; display:block;">
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.964 0L.165 13.233c-.457.778.091 1.767.982 1.767h13.706c.89 0 1.438-.99.982-1.767L8.982 1.566ZM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5Zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"/>
            </svg>
            <span style="flex:1;">Please check your email inbox!!</span>
          </div>
        </div>
      `,
      confirmButtonText: 'Confirm',
      focusConfirm: false,
      showCloseButton: true,
      didOpen: () => {
        const popup = Swal.getPopup()!;
        this.confirmMailInput = popup.querySelector('#email') as HTMLInputElement;
        this.confirmMailInput.onkeyup = (event) => event.key === 'Enter' && Swal.clickConfirm();
      },
      preConfirm: () => {
        const confirmMail = this.confirmMailInput?.value;
        if (!confirmMail) {
          Swal.showValidationMessage('Please enter email address');
          return false;
        }
        return true;
      }
    }).then((result) => {
      if (result.value) {
        Swal.close();
        this.userService.forgotPassword(this.marketId, this.confirmMailInput?.value)
          .pipe(take(1)).subscribe({
            next: (res: any) => {
              if (res.Success) {
                this.toastr.success(res.Message, $localize`Success`, this.toasterConfig);
              } else {
                this.toastr.error(res.Message, $localize`Error`, this.toasterConfig);
              }
            },
            error: (err) => {
              this.sharedService.handleApiError(err, 'Unable to reset password. Please try again.');
            }
          });
      }
    });
  }

  ngOnDestroy(): void {
    this.renderer.removeStyle(document.body, 'min-height');
    this.renderer.removeStyle(document.body, 'background');
    this.renderer.removeStyle(document.body, 'display');
    this.renderer.removeStyle(document.body, 'align-items');
    this.renderer.removeStyle(document.body, 'justify-content');
    this.renderer.removeStyle(document.body, 'font-family');
  }
}